/**
 * `host` elements: a box the page fills with its own DOM. The producer of the
 * ops (a React tree through @swiftbrowser/react's `Host`, or any page script)
 * registers an element under a key and emits `{ kind: "host", props: {
 * content: key, sizing } }`; the renderer adopts the element into the host's
 * box and the layout engine measures it (src/layout, `measureHost`). When
 * the content changes size on its own, `notifyHostContentChange` asks the
 * renderer to lay the tree out again. DOM-free apart from the element type,
 * so the registry can be imported by headless code.
 */

const contents = new Map<string, HTMLElement>();
const listeners = new Set<(key: string) => void>();

export function registerHostContent(key: string, element: HTMLElement): void {
  contents.set(key, element);
}

export function unregisterHostContent(key: string): void {
  contents.delete(key);
}

export function hostContent(key: string): HTMLElement | undefined {
  return contents.get(key);
}

/** Called with the key whose content changed (its size, most often); returns the unsubscribe. */
export function onHostContentChange(listener: (key: string) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyHostContentChange(key: string): void {
  for (const listener of listeners) listener(key);
}
